import { AlertType, VesselAlertsModel } from './vessel-alerts-model';
import { LoadstarService } from '../services/loadstar-service';
import { NotificationTypes } from '../services/types/vessel-notifications-services.types';
import { VesselNotificationsService } from '../services/vessel-notifications-service';
import { IMO, VoyageImoModel } from './voyage-imo-model';
import { EditableLoadstarValues, LOADSTAR_REVIEWED_ALERT_ID, LoadstarEvents, LoadstarModelBase, LoadstarNotificationType, LoadstarValues } from './loadstar-model';
import { VesselPlanningModel, VoyagePlan } from './vessel-planning-model';
import { deepCopy } from '../utils/helpers';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

export class LoadstarModelImpl extends LoadstarModelBase {
    private _exists: boolean | null = null;
    private _currentAlert: LoadstarNotificationType | null = null;
    private _pollingInterval?: ReturnType<typeof setInterval>;
    private imo = isVesselOnCloud ? VoyageImoModel?.imo : (<any>window)['appenv'].vesselIMONumber;
    constructor() {
        super();
        this.initialise();
    }

    get exists(): boolean | null {
        return this._exists;
    }

    async initialise(): Promise<void> {
        await this.checkExistsByImo(this.imo);
        this.setCompleteLifecycle();
    }

    async checkExistsByImo(imo: IMO = this.imo): Promise<boolean> {
        const exists = await LoadstarService.checkVesselExists(imo);
        this._exists = exists;

        if (exists) {
            await this.fetchLoadstarNotifications();
            this._startPolling();
        } else {
            this._stopPolling();
            this._currentAlert = null;
            this._removeLoadstarAlert();
        }

        this.dispatchEvent(new CustomEvent(LoadstarEvents.ExistsUpdated, {
            detail: { imo, exists },
        }));
        return exists;
    }

    async currentImoLoadstarExists(): Promise<boolean> {
        if (this._exists === null) {
            await this.checkExistsByImo();
            return this._exists || false;
        }
        return this._exists;
    }

    async getValueFromLoadstar(imo: IMO = VoyageImoModel?.imo ?? null, scheduleid: string, voyageNo: string = ''): Promise<LoadstarValues | null> {
        const values = await LoadstarService.getValuesFromLoadstar(imo, scheduleid, voyageNo);
        return values;
    }

    getCurrentAlert(): LoadstarNotificationType | null {
        return this._currentAlert;
    }

    async getLatestMatchingPlan(alert: LoadstarNotificationType | null = this._currentAlert): Promise<VoyagePlan | null> {
        if (!alert) {
            return null;
        }

        if (alert.data.schedule_id) {
            return await VesselPlanningModel!.getCurrentPlan(alert.data.schedule_id);
        }

        const allPlans = await VesselPlanningModel!.getAllPlans();
        const matchingPlans = allPlans.filter((plan) =>
            plan.departureTerminalCode?.slice(0, 5) === alert.data.departure_port &&
            plan.arrivalTerminalCode?.slice(0, 5) === alert.data.arrival_port,
        );
        matchingPlans.sort((a, b) => {
            const aTs = new Date(a.update_dtm || a.create_dtm || 0).getTime();
            const bTs = new Date(b.update_dtm || b.create_dtm || 0).getTime();
            return bTs - aTs;
        });

        return matchingPlans[0] || null;
    }

    createUpdatedPlan(
        plan: VoyagePlan,
        alert: LoadstarNotificationType | null = this._currentAlert,
        overrides: Partial<EditableLoadstarValues> = {},
    ): VoyagePlan {
        const nextPlan = deepCopy(plan);

        if (!alert) {
            return nextPlan;
        }

        nextPlan.foreDraft = overrides.fore_draft ?? alert.data.fore_draft;
        nextPlan.aftDraft = overrides.aft_draft ?? alert.data.aft_draft;
        nextPlan.gmt = overrides.fluid_gm ?? alert.data.fluid_gm;
        nextPlan.solidGm = overrides.solid_gm ?? alert.data.solid_gm;
        nextPlan.numberOfReefers = overrides.live_reefers ?? alert.data.live_reefers;
        nextPlan.rollAngle = overrides.max_roll_angle ?? alert.data.max_roll_angle;

        return nextPlan;
    }

    openReviewChanges(alertId?: string): void {
        this.dispatchEvent(new CustomEvent(LoadstarEvents.ReviewChanges, {
            detail: { alertId: alertId || this._currentAlert?.id },
            bubbles: true,
            composed: true,
        }));
    }

    dismissCurrentReview(alertId?: string, action?: 'Accepted' | 'Rejected'): void {
        const id = alertId || this._currentAlert?.id;
        if (id) {
            const savedAt = this._currentAlert?.data?.saved_at ?? this._currentAlert?.updatedAt ?? '';
            localStorage.setItem(LOADSTAR_REVIEWED_ALERT_ID, `${id}:${savedAt}`);
        }
        this._removeLoadstarAlert();
        VesselNotificationsService.acceptOrRejectLoadStarNotification(id || '', action || 'Rejected');
    }

    private _startPolling() {
        this._stopPolling();
        this._pollingInterval = setInterval(() => {
            this.fetchLoadstarNotifications();
        }, 15 * 60 * 1000);
    }

    private _stopPolling() {
        if (this._pollingInterval) {
            clearInterval(this._pollingInterval);
            this._pollingInterval = undefined;
        }
    }

    private async fetchLoadstarNotifications(): Promise<void> {
        if (!this._exists) {
            return;
        }

        const alerts = await VesselNotificationsService.fetchNotificationsByTypeAndState(NotificationTypes.Loadstar, 'Active');
        const latestAlert = [...alerts].sort((a, b) => new Date(b.updatedAt ?? '').getTime() - new Date(a.updatedAt ?? '').getTime())[0] ?? null;

        this._currentAlert = latestAlert;

        if (!latestAlert) {
            this._removeLoadstarAlert();
            return;
        }

        const reviewedAlertId = localStorage.getItem(LOADSTAR_REVIEWED_ALERT_ID);
        const alertDiscriminator = `${latestAlert.id}:${latestAlert.data?.saved_at ?? latestAlert.updatedAt ?? ''}`;
        if (reviewedAlertId === alertDiscriminator) {
            this._removeLoadstarAlert();
            return;
        }

        const vesselImo = Number(this.imo);
        VesselAlertsModel!.addAlert(vesselImo, {
            id: latestAlert.id,
            type: AlertType.LoadstarUpdated,
            title: 'Stowage Plan Updated',
            message: 'A new stowage plan has been received from LoadStar. Please review the changes.',
            time: latestAlert.data.saved_at || latestAlert.updatedAt || latestAlert.created_at,
        });
    }

    private _removeLoadstarAlert() {
        const vesselImo = Number(this.imo);
        VesselAlertsModel!.removeAlertsByType(vesselImo, AlertType.LoadstarUpdated);
    }
}
